import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { AutoComplete, Form, Spin, Tooltip, Typography } from 'antd';
import { linkFieldConfig } from '../../person/fields/utils/personFieldsUtils';
import { Locale } from '../../../../localization/LocalizationKeys';
import {
  EntityTypeEnum, FieldEnum,
  SitePersonCriteriaOrder,
  SuggestedLinkPersonStandardTitleQueryQuery, SuggestedLinkPersonStandardTitleQueryQueryVariables,
  UpdateLinkPersonTitleMutationMutation,
  UpdateLinkPersonTitleMutationMutationVariables
} from '../../../../gql/typings';
import { usePersonFieldMutation } from '../../person/Components/attributeFields/AttributeFields/usePersonFieldMutation';
import { TableFieldReturnedRecordPageType } from '../../search_old/types';

export const linkPersonTitleFieldConfig = linkFieldConfig(
  'personTitle',
  [
    'id',
    'person.id',
    'person.title',
    'person.countryCode',
    'person.controlSetting.id',
    'person.controlSetting.createDcr',
  ],
  ({ localization }) => ({
    title: localization.formatMessage(Locale.Attribute.Person_Title),
    additionalTableConfig: {
      width: 120
    },
    sorting: {
      ascend: SitePersonCriteriaOrder.PERSON_TITLE_ASC,
      descend: SitePersonCriteriaOrder.PERSON_TITLE_DESC,
    },
    dcrInfo: record => ({
      entityType: EntityTypeEnum.PERSON,
      entityAffiliationId: record.person.id,
      field: FieldEnum.HCP_TITLE,
    }),
    render: (record, { isViewingFromPage }) => {
      const personTitle = record.person.title;
      return (<Typography.Paragraph
        style={{ marginBottom: 0 }}
        ellipsis={{ rows: isViewingFromPage === TableFieldReturnedRecordPageType.DETAIL_PAGE ? 2 : 1 }}
      >
        <Tooltip placement='left' title={personTitle}>{personTitle}</Tooltip>
      </Typography.Paragraph>);
    },
    updateView: ({ endEditing, record }) => {
      const [title, setTitle] = useState(record.person.title ?? '');

      const {
        submit,
        Renderer,
        blocking,
      } = usePersonFieldMutation<UpdateLinkPersonTitleMutationMutation, UpdateLinkPersonTitleMutationMutationVariables>({
        mutation: UPDATE_MUTATION,
        controlSetting: record.person.controlSetting,
        endEditing
      });

      const onEndEditing = () => !blocking && endEditing();

      const {
        data: suggestedStandardTitlesData,
      } = useQuery<SuggestedLinkPersonStandardTitleQueryQuery, SuggestedLinkPersonStandardTitleQueryQueryVariables>(
        SUGGESTED_STANDARD_TITLE_QUERY,
        { variables: { countryCode: record.person.countryCode } }
      );

      const onSelect = (t: string) => {
        setTitle(t);
        onSaveChange(t);
      };

      const onChange = (t: string) => {
        setTitle(t);
      };

      const onSaveChange = (t: string) => !blocking && submit({ persId: record.person.id, title: t }).then(onEndEditing);

      return (
        <Renderer>
          <Form style={{ position: 'relative' }}>
            <AutoComplete
              style={{ width: '100%' }}
              options={(suggestedStandardTitlesData?.standardTitles?.nodes ?? []).map(it => ({
                value: it.label
              }))}
              autoFocus
              value={title}
              placeholder={localization.formatMessage(Locale.Command.Enter_title)}
              onSelect={onSelect}
              onChange={onChange}
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ width: 400, maxWidth: 600 }}
              filterOption={(inputValue, option) => option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1}
            />
            {blocking && <Spin size='small' style={{ position: 'absolute', top: '8px', right: '5px' }} />}
          </Form>
        </Renderer>
      );
    }
  })
);

const UPDATE_MUTATION = gql`
  mutation UpdateLinkPersonTitleMutation($persId: Int!, $title: String! ) {
    updatePersonTitle(persId: $persId, title: $title) {
      id
      title
    }
  }
`;

const SUGGESTED_STANDARD_TITLE_QUERY = gql`
  query SuggestedLinkPersonStandardTitleQuery($countryCode: String!) {
    standardTitles(criteria: {country: $countryCode, fetchSize: {limit: 1000}}) {
      hash
      nodes {
        id
        label
      }
    }
  }
`;
